import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { FcSearch } from "react-icons/fc";
import DatePicker from "react-datepicker";
import moment from "moment";
import { toast } from "react-toastify";
import Select, { MultiValue } from "react-select";

import { useLoading } from "../../../context/LoadingContext";
import { useReportTimesheetContext } from "../../../context/ReportTimsheetContex";
import useClientApi from "../../../api/client";
import useProjectApi from "../../../api/project";
import { getUserIdTokenForIonTime } from "../../../helper/api";
import {
  exportTimesheetAllPDF,
  TimesheetPdfPayload,
  WeekDayMeta,
  StatusHistoryItem,
  TimesheetTableRow,
} from "../../../helper/exportTimesheetPDF";
import { daysDataEnum, ReportStatusEnum } from "../../../helper/constant";
import type {
  DaysResponse,
  HistoryResponse,
  getTimeheetdetailResponseModel,
} from "../../../model/timesheet";
import type {
  getclientlistResponseModel,
  Project as ClientProject,
} from "../../../model/clientmodel";
import type {
  ProjectUser,
  ProjectDetailResponse,
} from "../../../model/projectModel";

type WeekWindow = { start: Date; end: Date };

type ProjectOption = {
  value: number;
  label: string;
};

type AggregatedProjectEmployee = {
  user: ProjectUser;
  projects: string[];
};

type EmployeeOption = {
  value: number;
  label: string;
  employeeName: string;
  // employeeLdap: string;
  employeeIontimeId: number;
  projects: string[];
};

const SELECT_ALL_EMPLOYEE_ID = -1;

const sanitizeFileName = (value: string) =>
  value.replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").trim();

const buildEmployeeOption = (
  aggregated: AggregatedProjectEmployee,
): EmployeeOption | null => {
  const { user, projects } = aggregated;
  const id = user["Employee id"];
  if (typeof id !== "number" || Number.isNaN(id)) {
    return null;
  }
  const name = user["Employee Name"]?.trim() ?? "";
  const ldap = user["Employee ldap"]?.trim() ?? "";
  // const projectList = projects.length > 0 ? ` • ${projects.join(", ")}` : "";
  const baseLabel = name && ldap ? `${name}` : name || ldap || `Employee ${id}`;
  return {
    value: id,
    label: `${baseLabel}`,
    employeeName: name,
    // employeeLdap: ldap,
    employeeIontimeId: user.Employee_iontime_id,
    projects,
  };
};

const toSunday = (date: Date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  normalized.setDate(normalized.getDate() - normalized.getDay());
  return normalized;
};

const toSaturday = (sunday: Date) => {
  const saturday = new Date(sunday);
  saturday.setDate(saturday.getDate() + 6);
  saturday.setHours(0, 0, 0, 0);
  return saturday;
};

const getWeekDates = (weekStart: Date) => {
  const dates: Date[] = [];
  for (let offset = 0; offset < 7; offset += 1) {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + offset);
    dates.push(date);
  }
  return dates;
};

const normalizeDateString = (value?: string) => {
  if (!value) {
    return "";
  }
  const parsed = moment(
    value,
    ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "YYYY/MM/DD"],
    true,
  );
  if (parsed.isValid()) {
    return parsed.format("DD-MM-YYYY");
  }
  const fallback = moment(value);
  return fallback.isValid() ? fallback.format("DD-MM-YYYY") : value;
};

const formatHours = (value?: string) => {
  if (!value) {
    return "0:00";
  }
  const trimmed = value.trim();
  if (
    !trimmed ||
    trimmed === "0" ||
    trimmed === "0:00" ||
    trimmed === "00:00"
  ) {
    return "0:00";
  }

  if (trimmed.includes(":")) {
    const [hours, minutes = "00"] = trimmed.split(":");
    return `${hours || "0"}:${minutes.padEnd(2, "0").slice(0, 2)}`;
  }

  if (trimmed.includes(".")) {
    const [hours, minutes = "0"] = trimmed.split(".");
    return `${hours || "0"}:${minutes.padEnd(2, "0").slice(0, 2)}`;
  }

  return `${trimmed}:00`;
};

const mapStatusHistory = (
  history?: { data?: HistoryResponse[] } | null,
): StatusHistoryItem[] => {
  if (!history?.data || history.data.length === 0) {
    return [];
  }

  return history.data.map((entry) => {
    const formattedDate = moment(entry.status_create_ts).isValid()
      ? moment(entry.status_create_ts).format("DD-MM-YYYY HH:mm:ss")
      : entry.status_create_ts;

    const base = {
      date: formattedDate,
      by: entry.status_user_id ?? "N/A",
    };

    if (
      entry.report_statuses_id === ReportStatusEnum.Approved ||
      entry.report_statuses_id === ReportStatusEnum.PartiallyApproved
    ) {
      return {
        type: "approved" as const,
        ...base,
        remark: entry.rejected_reason,
      };
    }

    if (entry.report_statuses_id === ReportStatusEnum.Returnedforreview) {
      return {
        type: "rejected" as const,
        ...base,
        remark: entry.rejected_reason,
        rejectedDates: entry.rejected_dates,
      };
    }

    return {
      type: "submitted" as const,
      ...base,
    };
  });
};

const deriveApprover = (history: StatusHistoryItem[]) => {
  const approvers = history
    .filter((item) => item.type === "approved")
    .map((item) => item.by)
    .filter((value) => value && value.trim().length > 0);
  if (approvers.length === 0) {
    return "N/A";
  }
  return Array.from(new Set(approvers)).join(", ");
};

const mapWeekMeta = (
  weekDates: Date[],
  response: getTimeheetdetailResponseModel | null,
): WeekDayMeta[] => {
  const holidayDates = new Set<string>();
  const rejectedDayNumbers = new Set<number>();

  response?.days_data?.forEach((item) => {
    if (item.day_types_id === daysDataEnum.HOLIDAY) {
      holidayDates.add(normalizeDateString(item.date));
    }
  });

  response?.values?.forEach((row) => {
    row.days_data?.forEach((day: DaysResponse) => {
      const normalizedDate = normalizeDateString(day.date);
      if (day.day_types_id === daysDataEnum.HOLIDAY) {
        holidayDates.add(normalizedDate);
      }
      if (day.rejected) {
        const parsed = moment(normalizedDate, "DD-MM-YYYY");
        if (parsed.isValid()) {
          rejectedDayNumbers.add(parsed.date());
        }
      }
    });
  });

  response?.rejected_data?.forEach((item) => {
    item.rejected_dates
      ?.split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .forEach((value) => {
        const numeric = Number(value);
        if (!Number.isNaN(numeric)) {
          rejectedDayNumbers.add(numeric);
        }
      });
  });

  return weekDates.map((date, index) => {
    const formattedDate = moment(date).format("DD-MM-YYYY");
    return {
      label: date.toLocaleDateString("en-US", { weekday: "short" }),
      formattedDate,
      dayOfMonth: date.getDate(),
      isWeekend: index === 0 || index === weekDates.length - 1,
      isHoliday: holidayDates.has(formattedDate),
      isRejected: rejectedDayNumbers.has(date.getDate()),
    };
  });
};

const mapTableRows = (
  response: getTimeheetdetailResponseModel | null,
  weekDates: Date[],
  projectFilterIds?: Set<number>,
): TimesheetTableRow[] => {
  if (!response?.values || response.values.length === 0) {
    return [];
  }

  const orderedDates = weekDates.map((date) =>
    moment(date).format("DD-MM-YYYY"),
  );

  return response.values
    .filter((row) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rowAsAny = row as any;

      if (projectFilterIds && projectFilterIds.size > 0) {
        const candidateProjectIdRaw =
          rowAsAny.project_id ??
          rowAsAny.projects_id ??
          rowAsAny["project-id"] ??
          rowAsAny.projectid ??
          null;
        const candidateProjectId = Number(candidateProjectIdRaw);
        if (
          candidateProjectIdRaw === null ||
          Number.isNaN(candidateProjectId) ||
          !projectFilterIds.has(candidateProjectId)
        ) {
          return false;
        }
      }

      const description = row.task_description?.trim() ?? "";
      const hasHours = row.days_data?.some((day) => {
        const hours = day.hours?.trim() ?? "";
        return (
          hours !== "" && hours !== "0" && hours !== "0:00" && hours !== "00:00"
        );
      });
      return description !== "" || hasHours;
    })
    .map((row) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rowAsAny = row as any;
      const projectName =
        rowAsAny["project-name"] ??
        rowAsAny.project_name ??
        rowAsAny.project ??
        row.activity ??
        "";

      const dayMap = new Map<string, string>();
      row.days_data?.forEach((day: DaysResponse) => {
        dayMap.set(normalizeDateString(day.date), day.hours);
      });

      const days = orderedDates.map((key) => formatHours(dayMap.get(key)));

      return {
        project: projectName,
        description: row.task_description ?? "",
        days,
      };
    });
};

const buildWeekWindows = (start: Date, end: Date): WeekWindow[] => {
  const windows: WeekWindow[] = [];
  const uniqueWeekStarts = new Set<number>();
  let cursor = toSunday(new Date(start));

  while (cursor.getTime() <= end.getTime()) {
    const weekStart = toSunday(new Date(cursor));
    const key = weekStart.getTime();
    if (!uniqueWeekStarts.has(key)) {
      uniqueWeekStarts.add(key);
      windows.push({ start: weekStart, end: toSaturday(weekStart) });
    }
    cursor = new Date(weekStart);
    cursor.setDate(cursor.getDate() + 7);
  }

  return windows;
};

const ProjectTimesheetsPage: React.FC = () => {
  const { isLoading } = useLoading();
  const { getTimesheetAllData } = useReportTimesheetContext();
  const { fetchGetClientList, fetchGetClientDetail } = useClientApi();
  const { fetchProjectDetail } = useProjectApi();
  const userId = getUserIdTokenForIonTime();
  const fetchClientListRef = useRef(fetchGetClientList);
  const fetchClientDetailRef = useRef(fetchGetClientDetail);
  const fetchProjectDetailRef = useRef(fetchProjectDetail);
  const projectRequestIdRef = useRef(0);

  const [weekRange, setWeekRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [clientOptions, setClientOptions] = useState<
    getclientlistResponseModel[]
  >([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [projectOptions, setProjectOptions] = useState<ClientProject[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<ProjectOption[]>([]);
  const [projectEmployees, setProjectEmployees] = useState<
    AggregatedProjectEmployee[]
  >([]);
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeOption[]>(
    [],
  );
  const [startDate, endDate] = weekRange;

  const formattedWeekRange = useMemo(() => {
    if (!startDate || !endDate) {
      return "";
    }
    return `${moment(startDate).format("DD MMM YYYY")} - ${moment(endDate).format("DD MMM YYYY")}`;
  }, [startDate, endDate]);

  const projectSelectOptions = useMemo<ProjectOption[]>(() => {
    if (!projectOptions || projectOptions.length === 0) {
      return [];
    }

    return projectOptions.map((project) => ({
      value: project.id,
      label: project.name || project.project_code || `Project ${project.id}`,
    }));
  }, [projectOptions]);
  const hasSelectedProjects = selectedProjects.length > 0;
  const employeeOptions = useMemo<EmployeeOption[]>(() => {
    if (!projectEmployees || projectEmployees.length === 0) {
      return [];
    }

    return projectEmployees
      .map(buildEmployeeOption)
      .filter((item): item is EmployeeOption => item !== null);
  }, [projectEmployees]);

  const employeeSelectOptions = useMemo<EmployeeOption[]>(() => {
    if (employeeOptions.length === 0) {
      return [];
    }

    const selectAllOption: EmployeeOption = {
      value: SELECT_ALL_EMPLOYEE_ID,
      label: "Select All Employees",
      employeeName: "Select All Employees",
      // employeeLdap: "",
      employeeIontimeId: SELECT_ALL_EMPLOYEE_ID,
      projects: [],
    };

    return [selectAllOption, ...employeeOptions];
  }, [employeeOptions]);

  const handleEmployeesChange = (options: MultiValue<EmployeeOption>) => {
    if (!options || options.length === 0) {
      setSelectedEmployees([]);
      return;
    }

    const includesSelectAll = options.some(
      (option) => option.value === SELECT_ALL_EMPLOYEE_ID,
    );

    if (includesSelectAll) {
      setSelectedEmployees(employeeOptions);
      return;
    }

    const sanitized = options.filter(
      (option) => option.value !== SELECT_ALL_EMPLOYEE_ID,
    );
    setSelectedEmployees(sanitized);
  };

  useEffect(() => {
    fetchClientListRef.current = fetchGetClientList;
    fetchClientDetailRef.current = fetchGetClientDetail;
    fetchProjectDetailRef.current = fetchProjectDetail;
  }, [fetchGetClientList, fetchGetClientDetail, fetchProjectDetail]);

  useEffect(() => {
    const loadClients = async () => {
      if (!userId) {
        return;
      }

      const numericUserId = Number(userId);
      if (Number.isNaN(numericUserId)) {
        toast.error("Invalid user context. Please re-login.");
        return;
      }

      try {
        const response = await fetchClientListRef.current(numericUserId, 1);
        setClientOptions(response ?? []);
      } catch (error) {
        console.error("Failed to load clients:", error);
        toast.error("Unable to load clients");
      }
    };

    loadClients();
  }, [userId]);

  const handleClientChange = async (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const value = event.target.value;
    const parsedClientId = Number(value);

    setSelectedClientId(
      value && !Number.isNaN(parsedClientId) ? parsedClientId : null,
    );
    setProjectOptions([]);
    setSelectedProjects([]);
    setProjectEmployees([]);
    setSelectedEmployees([]);

    if (!value || Number.isNaN(parsedClientId)) {
      return;
    }

    if (!userId) {
      toast.error("User context missing. Please re-login.");
      return;
    }

    try {
      const numericUserId = Number(userId);
      if (Number.isNaN(numericUserId)) {
        toast.error("Invalid user context. Please re-login.");
        return;
      }

      const clientDetails = await fetchClientDetailRef.current({
        user_id: numericUserId,
        client_id: parsedClientId,
        status: 0,
      });
      setProjectOptions(clientDetails?.projects ?? []);
    } catch (error) {
      console.error("Failed to load projects:", error);
      toast.error("Unable to load projects for selected client");
    }
  };

  const handleProjectChange = async (options: MultiValue<ProjectOption>) => {
    const nextProjects = [...options];
    const previousProjects = selectedProjects;
    const previousSelection = [...selectedEmployees];

    const nextProjectIdSet = new Set(
      nextProjects.map((project) => project.value),
    );
    const previousProjectIdSet = new Set(
      previousProjects.map((project) => project.value),
    );

    setSelectedProjects(nextProjects);

    if (nextProjects.length === 0) {
      setProjectEmployees([]);
      setSelectedEmployees([]);
      return;
    }

    const requestId = projectRequestIdRef.current + 1;
    projectRequestIdRef.current = requestId;

    const projectNameLookup = new Map<number, string>();
    projectOptions.forEach((project) => {
      const label =
        project.name || project.project_code || `Project ${project.id}`;
      projectNameLookup.set(project.id, label);
    });
    previousProjects.forEach((project) => {
      if (project.label) {
        projectNameLookup.set(project.value, project.label);
      }
    });
    nextProjects.forEach((project) => {
      if (project.label) {
        projectNameLookup.set(project.value, project.label);
      }
    });

    const employeeMap = new Map<
      number,
      { user: ProjectUser; projects: Set<string> }
    >();
    projectEmployees.forEach((entry) => {
      const employeeId = entry.user["Employee id"];
      if (typeof employeeId === "number" && !Number.isNaN(employeeId)) {
        employeeMap.set(employeeId, {
          user: entry.user,
          projects: new Set(entry.projects),
        });
      }
    });

    const removedProjectLabels = previousProjects
      .filter((project) => !nextProjectIdSet.has(project.value))
      .map(
        (project) =>
          projectNameLookup.get(project.value) ??
          project.label ??
          `Project ${project.value}`,
      );

    if (removedProjectLabels.length > 0) {
      employeeMap.forEach((entry, key) => {
        removedProjectLabels.forEach((label) => entry.projects.delete(label));
        if (entry.projects.size === 0) {
          employeeMap.delete(key);
        }
      });
    }

    const addedProjects = nextProjects.filter(
      (project) => !previousProjectIdSet.has(project.value),
    );

    try {
      if (addedProjects.length > 0) {
        const projectDetailsResults = await Promise.allSettled(
          addedProjects.map((project) =>
            fetchProjectDetailRef.current(project.value),
          ),
        );

        if (requestId !== projectRequestIdRef.current) {
          return;
        }

        projectDetailsResults.forEach((result, index) => {
          if (result.status !== "fulfilled" || !result.value) {
            return;
          }

          const projectId = addedProjects[index]?.value;
          if (!projectId) {
            return;
          }

          const details = result.value as ProjectDetailResponse;
          const projectName =
            projectNameLookup.get(projectId) ??
            addedProjects[index]?.label ??
            details.name ??
            details.project_code ??
            `Project ${projectId}`;

          details.users?.forEach((employee) => {
            const employeeId = employee["Employee id"];
            if (typeof employeeId !== "number" || Number.isNaN(employeeId)) {
              return;
            }

            const existing = employeeMap.get(employeeId);
            if (existing) {
              existing.projects.add(projectName);
              existing.user = employee;
            } else {
              employeeMap.set(employeeId, {
                user: employee,
                projects: new Set<string>([projectName]),
              });
            }
          });
        });
      }

      const aggregatedList: AggregatedProjectEmployee[] = Array.from(
        employeeMap.values(),
      )
        .map(({ user, projects }) => ({
          user,
          projects: Array.from(projects).sort((a, b) => a.localeCompare(b)),
        }))
        .sort((a, b) => {
          const nameA = a.user["Employee Name"]?.toLowerCase() ?? "";
          const nameB = b.user["Employee Name"]?.toLowerCase() ?? "";
          return nameA.localeCompare(nameB);
        });

      setProjectEmployees(aggregatedList);

      const retainedSelection = previousSelection
        .map((selected) =>
          aggregatedList.find(
            (employee) => employee.user["Employee id"] === selected.value,
          ),
        )
        .filter((employee): employee is AggregatedProjectEmployee =>
          Boolean(employee),
        )
        .map((employee) => buildEmployeeOption(employee))
        .filter((option): option is EmployeeOption => option !== null);

      setSelectedEmployees(retainedSelection);
    } catch (error) {
      console.error("Failed to load employees:", error);
      toast.error("Unable to load employees for selected projects");
      if (requestId === projectRequestIdRef.current) {
        setProjectEmployees([]);
        setSelectedEmployees([]);
      }
    }
  };

  const getDayClassName = (date: Date) => {
    if (!startDate || !endDate) {
      return "";
    }

    const normalizedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0,
      0,
    );

    const currentTime = normalizedDate.getTime();
    const startTime = startDate.getTime();
    const endTime = endDate.getTime();

    if (currentTime === startTime) {
      return "week-range-start";
    }
    if (currentTime === endTime) {
      return "week-range-end";
    }
    if (currentTime > startTime && currentTime < endTime) {
      return "week-range-day";
    }
    return "";
  };

  const fetchTimesheet = async (payload: {
    start_date: string;
    end_date: string;
    user_id: number;
  }): Promise<getTimeheetdetailResponseModel | null> => {
    try {
      const maybePromise = getTimesheetAllData(payload) as unknown as
        | Promise<getTimeheetdetailResponseModel | null | undefined>
        | getTimeheetdetailResponseModel
        | null
        | undefined;
      const resolved = await Promise.resolve(maybePromise);
      return resolved ?? null;
    } catch (error) {
      return null;
    }
  };

  const handleGenerateReport = async () => {
    if (selectedProjects.length === 0) {
      toast.error("Please select at least one project.");
      return;
    }

    if (selectedEmployees.length === 0) {
      toast.error("Please select at least one employee.");
      return;
    }

    if (!startDate || !endDate) {
      toast.error("Please choose a week range.");
      return;
    }

    if (startDate.getTime() > endDate.getTime()) {
      toast.error("Selected week range is invalid.");
      return;
    }

    const weekWindows = buildWeekWindows(startDate, endDate);
    const allExportPayloads: TimesheetPdfPayload[] = [];
    const projectIdSet = new Set(
      selectedProjects.map((project) => project.value),
    );
    const projectLabels = selectedProjects.map(
      (project) => project.label || `Project ${project.value}`,
    );
    const projectDescriptor =
      projectLabels.length === 1
        ? projectLabels[0]
        : `${projectLabels.length} Projects`;
    const summaryByEmployee: Array<{
      employeeId: number;
      employeeName: string;
      responses: Array<getTimeheetdetailResponseModel | null>;
      summaries: Array<{ period: string; rows: number }>;
    }> = [];
    const employeesWithoutData: string[] = [];

    for (const employee of selectedEmployees) {
      const employeeResponses: Array<getTimeheetdetailResponseModel | null> =
        [];
      const exportSummaries: Array<{ period: string; rows: number }> = [];
      const displayName =
        employee.employeeName ||
        // employee.employeeLdap ||
        `Employee ${employee.value}`;

      for (const window of weekWindows) {
        const payload = {
          start_date: moment(window.start).format("DD-MM-YYYY"),
          end_date: moment(window.end).format("DD-MM-YYYY"),
          user_id: employee.employeeIontimeId, // was: employee.value
        };

        // eslint-disable-next-line no-await-in-loop
        const response = await fetchTimesheet(payload);
        employeeResponses.push(response);

        const weekDates = getWeekDates(window.start);
        const weekMeta = mapWeekMeta(weekDates, response);
        const tableRows = mapTableRows(response, weekDates, projectIdSet);
        if (tableRows.length === 0) {
          continue;
        }
        const statusHistory = mapStatusHistory(
          response?.report_status_data ?? null,
        );
        const approverName = deriveApprover(statusHistory);
        const periodLabel = `${moment(window.start).format("DD MMM YYYY")} - ${moment(
          window.end,
        ).format("DD MMM YYYY")}`;

        allExportPayloads.push({
          period: periodLabel,
          employeeName: displayName,
          approverName,
          tableData: tableRows,
          weekMeta,
          statusHistory,
        });

        exportSummaries.push({
          period: periodLabel,
          rows: tableRows.length,
        });
      }

      if (exportSummaries.length === 0) {
        employeesWithoutData.push(displayName);
        continue;
      }

      summaryByEmployee.push({
        employeeId: employee.value,
        employeeName: displayName,
        responses: employeeResponses,
        summaries: exportSummaries,
      });
    }

    if (allExportPayloads.length === 0) {
      toast.info(
        "No timesheet data found for the selected project(s) and date range.",
      );
      return;
    }

    if (
      employeesWithoutData.length > 0 &&
      employeesWithoutData.length !== selectedEmployees.length
    ) {
      toast.info(
        `${employeesWithoutData.length} employee(s) had no timesheet data for the selected project(s) and date range.`,
      );
    }
    if (allExportPayloads.length > 0) {
      const startLabel = moment(startDate).format("DDMMMYYYY");
      const endLabel = moment(endDate).format("DDMMMYYYY");
      const baseName =
        selectedEmployees.length === 1
          ? sanitizeFileName(
              selectedEmployees[0].employeeName ||
                // selectedEmployees[0].employeeLdap ||
                `Employee-${selectedEmployees[0].value}`,
            )
          : `${selectedEmployees.length}-Employees`;
      const sanitizedProjectName = sanitizeFileName(projectDescriptor);
      const fileName = `Project-Timesheets-${sanitizedProjectName}-${baseName}-${startLabel}-to-${endLabel}.pdf`;
      exportTimesheetAllPDF(allExportPayloads, fileName);
    }

    console.log("Generated week windows:", weekWindows);
    console.log("Timesheet responses summary:", summaryByEmployee);
    console.log("Projects included:", projectLabels);
  };

  const refreshTable = () => {
    setWeekRange([null, null]);
    setSelectedClientId(null);
    setSelectedProjects([]);
    setProjectOptions([]);
    setProjectEmployees([]);
    setSelectedEmployees([]);
  };

  const isGenerateDisabled =
    selectedEmployees.length === 0 ||
    !hasSelectedProjects ||
    !startDate ||
    !endDate;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="max-w-7xl gap-x-6 gap-y-4 grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ">
        <div>
          <label
            htmlFor="client"
            className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400"
          >
            Client Name
          </label>
          <div className="mt-2">
            <select
              id="client"
              value={selectedClientId ?? ""}
              onChange={handleClientChange}
              className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
            >
              <option value="">Select Client</option>
              {clientOptions.map((client) => (
                <option key={client.id} value={client.id}>
                  {client["client name"]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label
            htmlFor="project"
            className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400"
          >
            Project Name
          </label>
          <div className="mt-2">
            <Select<ProjectOption, true>
              isMulti
              inputId="project-select"
              classNamePrefix="react-select"
              value={selectedProjects}
              onChange={handleProjectChange}
              options={projectSelectOptions}
              isDisabled={!selectedClientId}
              placeholder={
                selectedClientId ? "Select project(s)" : "Select client first"
              }
              noOptionsMessage={() =>
                selectedClientId ? "No projects found" : "Select client first"
              }
              closeMenuOnSelect={false}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  minHeight: "42px",
                  borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
                  boxShadow: state.isFocused ? "0 0 0 1px #2563eb" : "none",
                  "&:hover": {
                    borderColor: "#2563eb",
                  },
                  backgroundColor: state.isDisabled
                    ? "#f3f4f6"
                    : provided.backgroundColor,
                }),
                multiValue: (provided) => ({
                  ...provided,
                  backgroundColor: "#e5f1ff",
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: "#1e3a8a",
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  color: "#1e3a8a",
                  ":hover": {
                    backgroundColor: "#bfdbfe",
                    color: "#1e3a8a",
                  },
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="employee-select"
            className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400"
          >
            Employee Name
          </label>
          <div className="mt-2">
            <Select<EmployeeOption, true>
              isMulti
              inputId="employee-select"
              classNamePrefix="react-select"
              value={selectedEmployees}
              onChange={handleEmployeesChange}
              options={employeeSelectOptions}
              isDisabled={!hasSelectedProjects}
              placeholder={
                hasSelectedProjects
                  ? "Select employee(s)"
                  : "Select project first"
              }
              noOptionsMessage={() =>
                hasSelectedProjects
                  ? "No employees found"
                  : "Select project first"
              }
              closeMenuOnSelect={false}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  minHeight: "42px",
                  borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
                  boxShadow: state.isFocused ? "0 0 0 1px #2563eb" : "none",
                  "&:hover": {
                    borderColor: "#2563eb",
                  },
                  backgroundColor: state.isDisabled
                    ? "#f3f4f6"
                    : provided.backgroundColor,
                }),
                multiValue: (provided) => ({
                  ...provided,
                  backgroundColor: "#e5f1ff",
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: "#1e3a8a",
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  color: "#1e3a8a",
                  ":hover": {
                    backgroundColor: "#bfdbfe",
                    color: "#1e3a8a",
                  },
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="week-range"
            className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400"
          >
            Date (Start & End)
          </label>
          <div className="mt-2 space-y-2">
            <DatePicker
              selectsRange
              startDate={startDate ?? undefined}
              endDate={endDate ?? undefined}
              onChange={(dates) => setWeekRange(dates)}
              minDate={startDate ?? undefined}
              maxDate={endDate ?? undefined}
              placeholderText="Select Date Range"
              filterDate={(date) => {
                const day = date.getDay();
                return startDate ? day === 0 || day === 6 : day === 0;
              }}
              dayClassName={getDayClassName}
              className="custom-datepicker shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
              isClearable
            />
            {formattedWeekRange ? (
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Selected: {formattedWeekRange}
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {/* Pick any Sunday or Saturday; each selection expands to a full Sunday–Saturday week. */}
              </p>
            )}
          </div>
        </div>
        <div className="flex space-x-4 items-start">
          <button
            onClick={handleGenerateReport}
            disabled={isGenerateDisabled}
            className={`flex items-center px-4 py-2 mt-9 text-sm rounded-lg focus:outline-none text-white ${
              isGenerateDisabled
                ? "bg-blue-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-800"
            }`}
          >
            <FcSearch className="h-5 w-5 text-white mr-2" /> Generate Report
          </button>
          <button
            onClick={refreshTable}
            className="flex items-center px-4 py-2 mt-9 text-sm bg-red-700 hover:bg-red-800 text-white rounded-lg focus:outline-none"
          >
            <ArrowPathIcon className="h-5 w-5 text-white mr-2" /> Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectTimesheetsPage;




